/**
 * First-party auth seam over `oidc-client-ts`'s `UserManager`.
 *
 * Specs mock this module rather than the vendor package, and the token mapping
 * below is the one place that mapping happens.
 */

import { Log, UserManager, type User, type UserManagerSettings } from 'oidc-client-ts';

import awsconfig from '../aws-config';

/**
 * oidc-client-ts defaults to a no-op logger, so every diagnostic it produces is discarded — and it
 * reports the interesting failures ONLY through that logger before rethrowing a bare `null`
 * (`logger.throw` then `throw null`, nine sites in 3.5.0). A sign-in that dies in
 * `_processSigninState` — state mismatch, authority mismatch, an `error` param from the provider —
 * therefore reaches our catch as `null`, which logs as "auth bootstrap failed" with no cause and no
 * stack. That is exactly what a failing preview sign-in looked like from the console.
 *
 * NOT DEBUG: `_useRefreshToken` logs the whole token response, putting live tokens in the console
 * on every silent renew. `logger.throw` reports at ERROR, so nothing above is lost.
 */
Log.setLogger(console);
Log.setLevel(Log.WARN);

/** The three values the app takes from a session. */
export interface AuthSession {
  /**
   * The ACCESS token — never the ID token. The backend sits behind an API Gateway
   * Cognito authorizer configured against this pool's custom scopes
   * (`user/*.read`, `user/*.write`), which rejects an ID token.
   */
  accessToken: string;
  /**
   * From the ID TOKEN's claims (`user.profile`) — Cognito access tokens carry no
   * `email`.
   *
   * `fhir-proxy-security`'s pre-signup trigger resolves a FHIR `Practitioner` by
   * email rather than by Cognito `sub`, so identity continuity rests on this claim.
   */
  email: string;
  /**
   * Expiry as epoch MILLISECONDS. `oidc-client-ts`'s `expires_at` is epoch
   * SECONDS; the `* 1000` in `toAuthSession` is that conversion.
   */
  expiresAtMs: number;
}

/**
 * Thrown when a session exists but cannot be mapped to a usable `AuthSession`.
 * Callers treat a rejected session read as "no session".
 */
export class AuthSessionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthSessionError';
  }
}

/**
 * Render a caught value as something a reader can act on.
 *
 * `String(cause)` is not enough: oidc-client-ts throws `null` after logging the real reason, and an
 * `ErrorResponse` carries its detail on properties rather than in `message`, so the default
 * stringification of either says nothing.
 */
function describeCause(cause: unknown): string {
  if (cause === null || cause === undefined) {
    return 'nothing — the library threw an empty value after logging the reason itself';
  }
  if (cause instanceof Error) {
    return cause.message;
  }
  return typeof cause === 'string' ? cause : JSON.stringify(cause);
}

/** Map an `oidc-client-ts` `User` onto the app's session contract. Exported for tests. */
export function toAuthSession(user: User): AuthSession {
  // `expires_at` is optional in oidc-client-ts. The app's expiry machinery (refresh
  // loop, expiry popup, daily reset) treats a missing expiry as "expired", so an
  // absent value cannot be defaulted to "far future" without disabling all three.
  if (user.expires_at === undefined) {
    throw new AuthSessionError('Session has no expires_at; cannot determine token expiry.');
  }

  // `email` is absent only if the `email` scope was not granted, which would break
  // Practitioner resolution entirely — hence a throw rather than an empty string.
  const email = user.profile.email;
  if (typeof email !== 'string' || email.length === 0) {
    throw new AuthSessionError('Session ID token carries no email claim.');
  }

  return {
    accessToken: user.access_token,
    email,
    expiresAtMs: user.expires_at * 1000,
  };
}

const oauth = awsconfig.Auth.oauth;

/**
 * Read a required build-time env value, failing loudly when it is absent.
 *
 * Without this, a missing variable builds an authorize URL with
 * `client_id=undefined` and Cognito answers with an error page that names no
 * variable. These are declared `requiredBuildEnv` in `service.manifest.json`, so
 * this guard covers only the local and preview paths that skip that check.
 */
function requireEnv(value: string | undefined, name: string): string {
  if (!value) {
    throw new Error(`${name} is not set; Cognito sign-in cannot be configured.`);
  }
  return value;
}

/**
 * The OIDC issuer, which is what `authority` must be — NOT the user-pool domain.
 *
 * Cognito serves the discovery document from the `cognito-idp` host, and it
 * already points `authorization_endpoint`, `token_endpoint` and
 * `userinfo_endpoint` at the pool DOMAIN, so no endpoint overrides are needed.
 *
 * KNOWN TRAP, unresolved here. If this pool has BOTH a custom domain and a prefix
 * domain, AWS serves discovery only from the custom domain and states the prefix
 * domain then "doesn't have discovery or token-signing-key endpoints". The pool is
 * owned by `fhir-proxy-security`, which is not readable from this repository, so
 * which domains it has could not be established. Discovery is driven entirely by
 * the issuer below, so if it turns out to be wrong the correction is this one
 * expression.
 */
function buildAuthority(): string {
  const region = requireEnv(awsconfig.Auth.region, 'VITE_REGION');
  const userPoolId = requireEnv(awsconfig.Auth.userPoolId, 'VITE_USER_POOL');
  return `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`;
}

/**
 * Built in a function rather than at module scope so a spec can construct settings
 * without a `UserManager` (which touches `window.location` and storage), and so the
 * `requireEnv` throws happen on first use rather than at import.
 */
export function buildUserManagerSettings(): UserManagerSettings {
  return {
    authority: buildAuthority(),
    client_id: requireEnv(awsconfig.Auth.userPoolWebClientId, 'VITE_CLIENTID'),
    redirect_uri: requireEnv(oauth.redirectSignIn, 'VITE_REDIRECT_SIGNIN_URL'),

    // No `post_logout_redirect_uri`: Cognito's `/logout` does not read it, and
    // `oidc-client-ts` emits `client_id` only alongside it — which `signOut` must pass
    // itself, so leaving this set puts `client_id` in the URL twice.

    // Authorization code + PKCE. This grant returns a refresh token, so silent
    // renew uses one rather than a hidden iframe.
    response_type: 'code',
    scope: oauth.scope.join(' '),

    // `getUser()` does not renew; this keeps the token fresh between the explicit
    // refreshes the token loop performs. Common guidance says to disable this for Cognito
    // (no `prompt=none`); it does not apply, because `signinSilent()` takes the
    // refresh-token grant whenever one is stored and `response_type: 'code'` always
    // stores one.
    automaticSilentRenew: true,

    // Cognito's `/oauth2/userInfo` returns claims already in the ID token, and
    // `email` — the only claim this app reads — is among them.
    loadUserInfo: false,

    // Cognito's discovery document omits `check_session_iframe`, so session
    // monitoring has nothing to poll and only logs errors.
    monitorSession: false,

    // Without this a signed-out refresh token still mints access tokens for 30 days.
    revokeTokensOnSignout: true,

    // NOT the default `['access_token', 'refresh_token']`: Cognito 400s
    // `unsupported_token_type` on access-token revocation and tries it first, and the library
    // treats a failed revoke as a failed sign-out — so the default abandons every redirect.
    // See authts/oidc-client-ts#262.
    revokeTokenTypes: ['refresh_token'],
  };
}

let cachedManager: UserManager | undefined;

/**
 * Lazily construct the process-wide `UserManager`. Lazy because its constructor
 * reads `window.location` and storage, which at module scope would run on every
 * import — including in specs that mock this module but still execute its top level.
 */
function getUserManager(): UserManager {
  cachedManager ??= new UserManager(buildUserManagerSettings());
  return cachedManager;
}

/**
 * The sign-out retry's manager. Uncached, and renewal is off: when the retry also fails the page
 * does not navigate, and this manager would otherwise keep renewing against storage the first one
 * has already cleared.
 */
function getUserManagerWithoutRevocation(): UserManager {
  return new UserManager({
    ...buildUserManagerSettings(),
    revokeTokensOnSignout: false,
    automaticSilentRenew: false,
  });
}

/**
 * Read the stored session WITHOUT contacting the provider.
 *
 * Returns `null` rather than throwing when there is no user, because "not signed
 * in" is an ordinary state at app bootstrap.
 */
export async function getCurrentSession(): Promise<AuthSession | null> {
  const user = await getUserManager().getUser();
  if (!user) return null;
  return toAuthSession(user);
}

/**
 * Obtain a FRESH session, renewing against the provider.
 *
 * Distinct from `getCurrentSession`: `getUser()` reads, `signinSilent()` renews.
 * Rejects rather than returning `null` when renewal fails.
 */
export async function refreshSession(): Promise<AuthSession> {
  const user = await getUserManager().signinSilent();
  if (!user) {
    throw new AuthSessionError('Silent renew returned no user.');
  }
  return toAuthSession(user);
}

/**
 * Begin interactive sign-in by redirecting to the provider.
 *
 * No `extraQueryParams`, so this hits `/oauth2/authorize` WITHOUT an
 * `identity_provider` parameter and lands on Cognito's managed login page showing
 * a single "Continue with Google" button. Google is the only IdP on this pool.
 */
export async function signInRedirect(): Promise<void> {
  await getUserManager().signinRedirect();
}

/**
 * Complete sign-in on return from the provider.
 *
 * `signinCallback` exchanges the redirect's `?code=&state=` at the token endpoint
 * and stores the user. Returns `null` when the current URL is not a sign-in
 * response, so bootstrap can call it unconditionally.
 */
export async function completeSignInRedirect(): Promise<AuthSession | null> {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('code') && !params.has('error')) {
    // Logged, not silent: "no code in the URL" and "the exchange failed" produce the SAME signed-out
    // screen, and telling them apart from the outside was what made this take several rounds. If a
    // sign-in redirect lands here, the code was gone before this ran — a different defect from the
    // exchange failing, and one no amount of provider-side logging would show.
    console.info(
      'care-betty: no authorization code in the URL, treating as an ordinary page load',
      window.location.search === '' ? '(empty query string)' : window.location.search,
    );
    return null;
  }

  let user: User | undefined;
  try {
    user = await getUserManager().signinCallback();
  } catch (cause) {
    // oidc-client-ts rethrows `null` after reporting the real message to its own logger
    // (`logger.throw(new Error(...)); throw null`). An unwrapped `null` reaches our caller's catch
    // as a valueless rejection, so the reason is stated here instead.
    throw new AuthSessionError(
      `Sign-in callback failed at ${window.location.pathname}${window.location.search}. ` +
        `oidc-client-ts reported: ${describeCause(cause)}`,
    );
  }

  // Strip `code`/`state` from the address bar. Without this a reload replays a
  // now-consumed authorization code, and the second exchange fails.
  window.history.replaceState({}, document.title, window.location.pathname);

  if (!user) {
    // Unreachable through `signinRedirect`: `signinCallback` dispatches on the stored
    // `request_type` and only the popup and silent branches return undefined, both of which
    // require a `silent_redirect_uri` this app never sets. Reaching here means the stored state
    // was written by a flow this app does not start — worth a loud report rather than the silent
    // `null` that made a failing sign-in indistinguishable from an ordinary page load.
    throw new AuthSessionError(
      'Sign-in callback resolved no user despite a code in the URL; the stored request_type was not a redirect sign-in.',
    );
  }

  return toAuthSession(user);
}

/**
 * Clear the local session AND the Cognito pool session, then return to the app.
 *
 * Clearing local tokens alone is not sign-out: the pool's session cookie survives, so
 * the next `/oauth2/authorize` re-authenticates the same user silently and returns —
 * indistinguishable from the Login button doing nothing, which is how this was
 * reported.
 *
 * COGNITO'S `/logout` IS NOT OIDC RP-INITIATED LOGOUT, so both parameters below are
 * passed explicitly rather than left to the library. It redirects to `logout_uri`,
 * which it does not read from the standard `post_logout_redirect_uri`, and BOTH are
 * required: measured against the pool, omitting `client_id` lands on Cognito's own
 * `/login` page instead of returning to the app. See authts/oidc-client-ts#1385.
 *
 * `LogoutURLs` IS MATCHED AS AN EXACT STRING, trailing slash included, so
 * `VITE_REDIRECT_SIGNOUT_URL` must be byte-identical to the entry registered in
 * `fhir-proxy-security` — a constraint that lives in another repository and is
 * invisible here. The preview client registers
 * `https://care-betty-preview.nrxinternal.com` with NO trailing slash; `AUDIT.md` §2.10
 * records the reading and the command that produced it.
 *
 * DO NOT ADD `removeUser()` BEFORE THE REDIRECT. `_signoutStart` calls it itself, after
 * loading the user to revoke its tokens; clearing first makes that load return `null` and
 * skips revocation silently.
 *
 * The retry exists because the library rethrows a failed revoke, which would otherwise
 * abandon the redirect and leave the browser looking signed in. It cannot help a failure
 * raised after `removeUser()` — a discovery fetch or a missing `end_session_endpoint` — so it
 * rethrows rather than reporting a cause it has not established.
 */
export async function signOut(): Promise<void> {
  const extraQueryParams = {
    logout_uri: requireEnv(oauth.redirectSignOut, 'VITE_REDIRECT_SIGNOUT_URL'),
    client_id: requireEnv(awsconfig.Auth.userPoolWebClientId, 'VITE_CLIENTID'),
  };

  try {
    await getUserManager().signoutRedirect({ extraQueryParams });
    return;
  } catch (cause) {
    console.error(
      `care-betty: sign-out redirect failed (${describeCause(cause)}); retrying without revocation`,
      cause,
    );
  }

  try {
    await getUserManagerWithoutRevocation().signoutRedirect({ extraQueryParams });
  } catch (cause) {
    throw new AuthSessionError(
      `Sign-out redirect failed twice; the Cognito session may survive. ${describeCause(cause)}`,
    );
  }
}
